import { useState, useEffect } from 'react';
import { useCharacterStore } from '../stores/characterStore';
import { useDiceStore } from '../stores/diceStore';
import { useChatStore } from '../stores/chatStore';
import { useCombatStore } from '../stores/combatStore';
import { useInitiativeStore } from '../stores/initiativeStore';
import { useNpcStore } from '../stores/npcStore';
import { useIsGM } from '../hooks/useIsGM';
import { Icon, type IconName } from './Icon';
import { openCharacterSheet, useOpenSheetStore } from '../stores/windowStore';
import type { Character, CharacterSummary } from '@machina/core';
import { getSpell, parseSpellAttackType, parseSpellSaveType, parseSpellDamage, scaleCantripDamage, parseSpellHealing } from '@machina/core';
import type { SaveAbility, SpellDamage, SpellHealing } from '@machina/core';
import { getBadgeById, getDefaultBadge, type Badge } from './badges';
import { BadgePicker } from './BadgePicker';

// Get badge for a character, falling back to defaults
function getCharacterBadge(badgeId?: string, index: number = 0): Badge {
  if (badgeId) {
    const badge = getBadgeById(badgeId);
    if (badge) return badge;
  }
  return getDefaultBadge(index);
}

// =============================================================================
// CLASS ICONS
// =============================================================================

const CLASS_ICONS: Record<string, IconName> = {
  Fighter: 'sword',
  Wizard: 'wand',
  Ranger: 'target',
  Cleric: 'sparkle',
  Rogue: 'skull',
  Barbarian: 'attack',
  Paladin: 'shield',
  Warlock: 'moon',
  Sorcerer: 'crystal',
  Bard: 'horn',
  Druid: 'heart',
  Monk: 'star',
};

function getClassIcon(classDisplay: string): IconName {
  for (const [className, icon] of Object.entries(CLASS_ICONS)) {
    if (classDisplay.includes(className)) return icon;
  }
  return 'token';
}

function getModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

function formatModifier(mod: number): string {
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

// D&D 5e skill list with associated abilities
const SKILLS: { name: string; ability: 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma' }[] = [
  { name: 'Acrobatics', ability: 'dexterity' },
  { name: 'Animal Handling', ability: 'wisdom' },
  { name: 'Arcana', ability: 'intelligence' },
  { name: 'Athletics', ability: 'strength' },
  { name: 'Deception', ability: 'charisma' },
  { name: 'History', ability: 'intelligence' },
  { name: 'Insight', ability: 'wisdom' },
  { name: 'Intimidation', ability: 'charisma' },
  { name: 'Investigation', ability: 'intelligence' },
  { name: 'Medicine', ability: 'wisdom' },
  { name: 'Nature', ability: 'intelligence' },
  { name: 'Perception', ability: 'wisdom' },
  { name: 'Performance', ability: 'charisma' },
  { name: 'Persuasion', ability: 'charisma' },
  { name: 'Religion', ability: 'intelligence' },
  { name: 'Sleight of Hand', ability: 'dexterity' },
  { name: 'Stealth', ability: 'dexterity' },
  { name: 'Survival', ability: 'wisdom' },
];

// =============================================================================
// PARTY MEMBER ROW
// =============================================================================

interface PartyMemberRowProps {
  summary: CharacterSummary;
  isSelected: boolean;
  onShowFlyout: () => void;
  onToggleAbsent: () => void;
  index: number;
}

function PartyMemberRow({ summary, isSelected, onShowFlyout, onToggleAbsent, index }: PartyMemberRowProps) {
  const [showAbsentToggle, setShowAbsentToggle] = useState(false);
  const sheetOpen = useOpenSheetStore((s) => s.isSheetOpen(summary.id));
  const hpPercent = Math.round((summary.currentHp / summary.maxHp) * 100);
  const hpColor =
    hpPercent >= 75
      ? 'var(--color-success)'
      : hpPercent >= 50
        ? 'hsl(80, 60%, 45%)'
        : hpPercent >= 25
          ? 'var(--color-warning)'
          : 'var(--color-error)';

  const badge = getCharacterBadge(summary.badgeId, index);
  const isAbsent = summary.absentThisSession;

  return (
    <div
      onClick={onShowFlyout}
      onMouseEnter={() => setShowAbsentToggle(true)}
      onMouseLeave={() => setShowAbsentToggle(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: isSelected ? 'var(--color-primary-subtle)' : 'transparent',
        border: isSelected
          ? 'var(--border-width-thin) solid var(--color-primary)'
          : 'var(--border-width-thin) solid transparent',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
        opacity: isAbsent ? 0.5 : 1,
      }}
    >
      {/* Badge icon with badge color — pulses when sheet is open in popup */}
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: summary.isNpc ? 'rgba(192, 192, 192, 0.15)' : badge.color,
          border: `2px solid ${summary.isNpc ? '#c0c0c0' : badge.accent}`,
          boxShadow: sheetOpen
            ? `0 0 8px ${badge.accent}, 0 0 16px ${badge.accent}60`
            : summary.isNpc ? '0 0 4px rgba(192, 192, 192, 0.3)' : `0 0 4px ${badge.color}40`,
          animation: sheetOpen ? 'badgeLightUp 0.4s ease-out forwards' : 'none',
        }}
      >
        <Icon
          name={summary.isNpc ? 'crown' : ((summary.badgeIcon as IconName) || getClassIcon(summary.classDisplay))}
          size={14}
          color={summary.isNpc ? '#c0c0c0' : badge.text}
        />
      </div>

      {/* Name and class */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {summary.name}
          {summary.isNpc && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.5px',
                padding: '1px 4px',
                borderRadius: 2,
                backgroundColor: summary.npcControlMode === 'ai'
                  ? 'rgba(147, 51, 234, 0.2)'
                  : 'rgba(192, 192, 192, 0.15)',
                color: summary.npcControlMode === 'ai' ? '#a855f7' : '#c0c0c0',
                border: `1px solid ${summary.npcControlMode === 'ai' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(192, 192, 192, 0.25)'}`,
              }}
            >
              {summary.npcControlMode === 'ai' ? 'AI' : 'NPC'}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary.race} {summary.classDisplay.split(' / ')[0]}
        </div>
      </div>

      {/* HP bar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        {isAbsent ? (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
            }}
          >
            Absent
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-mono)',
                color: hpColor,
              }}
            >
              {summary.currentHp}/{summary.maxHp}
            </div>
            <div
              style={{
                width: 40,
                height: 4,
                backgroundColor: 'var(--color-surface)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${hpPercent}%`,
                  height: '100%',
                  backgroundColor: hpColor,
                  transition: 'width var(--transition-normal)',
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Absent toggle button - shows on hover */}
      {showAbsentToggle && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleAbsent();
          }}
          title={isAbsent ? 'Mark as present' : 'Mark as absent this session'}
          style={{
            background: isAbsent ? 'var(--color-success)' : 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px 6px',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
            color: isAbsent ? '#fff' : 'var(--color-text-secondary)',
            transition: 'all var(--transition-fast)',
          }}
        >
          <Icon name={isAbsent ? 'check' : 'moon'} size={12} />
        </button>
      )}

    </div>
  );
}

// =============================================================================
// ELRIC-INSPIRED DECORATIVE ELEMENTS
// Dark, decadent Melnibonéan aesthetic with Chaos motifs
// =============================================================================

// 8-pointed Chaos Star - central motif
function ChaosStar({ size = 24, color = '#9333ea', opacity = 0.6 }: { size?: number; color?: string; opacity?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ opacity }}>
      {/* 8 arrows pointing outward */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <path
          key={angle}
          d="M12 12 L12 2 L10 5 M12 2 L14 5"
          stroke={color}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          transform={`rotate(${angle} 12 12)`}
        />
      ))}
      <circle cx="12" cy="12" r="2" fill={color} />
    </svg>
  );
}

// Melnibonéan corner flourish - angular, sinister, dragon-inspired
function CornerFlourish({ position, color }: { position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'; color: string }) {
  const transforms: Record<string, string> = {
    'top-left': 'scale(1, 1)',
    'top-right': 'scale(-1, 1)',
    'bottom-right': 'scale(-1, -1)',
    'bottom-left': 'scale(1, -1)',
  };
  const positions: Record<string, React.CSSProperties> = {
    'top-left': { top: 0, left: 0 },
    'top-right': { top: 0, right: 0 },
    'bottom-right': { bottom: 0, right: 0 },
    'bottom-left': { bottom: 0, left: 0 },
  };

  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      style={{
        position: 'absolute',
        ...positions[position],
        transform: transforms[position],
        opacity: 0.35,
        pointerEvents: 'none',
      }}
    >
      {/* Angular dragon-scale pattern */}
      <path
        d="M0 0 L0 24 L4 20 L4 12 L8 8 L16 8 L20 4 L24 0 Z"
        fill={color}
      />
      {/* Inner detail lines */}
      <path
        d="M2 2 L2 18 M6 6 L6 14 L12 8"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.5"
      />
      {/* Chaos arrow accent */}
      <path
        d="M8 0 L12 8 L16 0"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}

// Ornate section divider with Chaos symbolism
function SectionDivider({ color }: { color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0', opacity: 0.6 }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${color} 70%, ${color})` }} />
      <ChaosStar size={16} color={color} opacity={1} />
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${color} 70%, ${color})` }} />
    </div>
  );
}

// Runic border decoration
function RunicBorder({ color, vertical = false }: { color: string; vertical?: boolean }) {
  const runes = '᛫ᚱᚢᚾ᛫ᛁᚲ᛫'; // Elder Futhark-inspired
  return (
    <div
      style={{
        position: 'absolute',
        [vertical ? 'top' : 'left']: 0,
        [vertical ? 'bottom' : 'right']: 0,
        [vertical ? 'left' : 'top']: vertical ? 4 : 'auto',
        [vertical ? 'right' : 'bottom']: vertical ? 'auto' : 4,
        writingMode: vertical ? 'vertical-rl' : 'horizontal-tb',
        fontSize: 8,
        letterSpacing: 4,
        color: color,
        opacity: 0.2,
        fontFamily: 'serif',
        pointerEvents: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {runes.repeat(8)}
    </div>
  );
}

// Robert Gould-inspired ability frame - ornate pen & ink style
function AbilityShield({
  ability,
  score,
  modifier,
  isProficient,
  accentColor
}: {
  ability: string;
  score: number;
  modifier: number;
  isProficient: boolean;
  accentColor: string;
}) {
  const glowColor = isProficient ? accentColor : 'transparent';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '4px 2px 8px',
      }}
    >
      {/* Ornate frame with Art Nouveau curves */}
      <svg
        width="52"
        height="64"
        viewBox="0 0 52 64"
        style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }}
      >
        <defs>
          <filter id={`glow-${ability}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feFlood floodColor={glowColor} floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Outer ornate frame - flowing organic shape */}
        <path
          d="M26 2
             C32 2 38 4 42 8
             Q48 14 48 24
             L48 36
             Q48 48 40 54
             Q32 60 26 62
             Q20 60 12 54
             Q4 48 4 36
             L4 24
             Q4 14 10 8
             C14 4 20 2 26 2 Z"
          fill="rgba(10, 8, 12, 0.9)"
          stroke={isProficient ? accentColor : 'rgba(120, 100, 140, 0.4)'}
          strokeWidth={isProficient ? 1.5 : 0.75}
          filter={isProficient ? `url(#glow-${ability})` : undefined}
        />
        {/* Inner decorative ring */}
        <ellipse
          cx="26"
          cy="32"
          rx="18"
          ry="22"
          fill="none"
          stroke="rgba(120, 100, 140, 0.2)"
          strokeWidth="0.5"
          strokeDasharray="2 3"
        />
        {/* Top flourish */}
        <path
          d="M20 6 Q26 10 32 6"
          fill="none"
          stroke={isProficient ? accentColor : 'rgba(120, 100, 140, 0.3)'}
          strokeWidth="1"
        />
        {/* Bottom point accent */}
        <path
          d="M22 56 L26 60 L30 56"
          fill="none"
          stroke={isProficient ? accentColor : 'rgba(120, 100, 140, 0.3)'}
          strokeWidth="1"
        />
      </svg>
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', paddingTop: 8 }}>
        <div style={{
          fontSize: 8,
          fontWeight: 600,
          letterSpacing: '1.5px',
          color: isProficient ? accentColor : 'rgba(180, 160, 200, 0.7)',
          marginBottom: 4,
          fontFamily: 'serif',
          fontStyle: 'italic',
        }}>
          {ability.slice(0, 3).toUpperCase()}
        </div>
        <div style={{
          fontSize: 20,
          fontWeight: 300,
          lineHeight: 1,
          fontFamily: 'serif',
          color: isProficient ? '#fff' : 'rgba(220, 210, 230, 0.9)',
          textShadow: isProficient ? `0 0 8px ${accentColor}` : 'none',
        }}>
          {score}
        </div>
        <div style={{
          fontSize: 11,
          fontWeight: 500,
          color: modifier >= 0 ? 'rgba(134, 239, 172, 0.9)' : 'rgba(248, 113, 113, 0.9)',
          marginTop: 4,
          fontFamily: 'var(--font-mono)',
        }}>
          {modifier >= 0 ? '+' : ''}{modifier}
        </div>
      </div>
    </div>
  );
}

// Dark section with subtle organic border - Gould's flowing ink style
function ParchmentSection({
  title,
  children,
  icon,
  accentColor = '#9333ea'
}: {
  title?: string;
  children: React.ReactNode;
  icon?: IconName;
  accentColor?: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        padding: '18px 16px',
        marginBottom: '16px',
        background: 'linear-gradient(180deg, rgba(20, 15, 30, 0.6) 0%, rgba(10, 8, 15, 0.8) 100%)',
        borderRadius: '4px',
        border: '1px solid rgba(100, 80, 120, 0.2)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), 0 4px 12px rgba(0,0,0,0.3)',
      }}
    >
      {/* Subtle corner accents */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: 20,
        height: 20,
        borderTop: `1px solid ${accentColor}40`,
        borderLeft: `1px solid ${accentColor}40`,
        borderTopLeftRadius: 4,
        opacity: 0.6,
      }} />
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 20,
        height: 20,
        borderTop: `1px solid ${accentColor}40`,
        borderRight: `1px solid ${accentColor}40`,
        borderTopRightRadius: 4,
        opacity: 0.6,
      }} />

      {title && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: '1px solid rgba(100, 80, 120, 0.15)',
        }}>
          {icon && (
            <div style={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: `${accentColor}20`,
            }}>
              <Icon name={icon} size={12} color={accentColor} />
            </div>
          )}
          <span style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: 'rgba(180, 160, 200, 0.8)',
            fontFamily: 'serif',
          }}>
            {title}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

// =============================================================================
// CHARACTER FLYOUT
// =============================================================================

interface CharacterFlyoutProps {
  character: Character;
  onClose: () => void;
  index: number;
}

function CharacterFlyout({ character, onClose, index }: CharacterFlyoutProps) {
  const damageCharacter = useCharacterStore((s) => s.damageCharacter);
  const healCharacter = useCharacterStore((s) => s.healCharacter);
  const updateCharacter = useCharacterStore((s) => s.updateCharacter);
  const removeCharacter = useCharacterStore((s) => s.removeCharacter);
  const deleteFromSupabase = useCharacterStore((s) => s.deleteFromSupabase);
  const setBadge = useCharacterStore((s) => s.setBadge);
  const setBadgeIcon = useCharacterStore((s) => s.setBadgeIcon);
  const [showBadgePicker, setShowBadgePicker] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const isGM = useIsGM();

  // Dice rolling
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);
  const addSpellLookup = useChatStore((s) => s.addSpellLookup);
  const addSavePrompt = useChatStore((s) => s.addSavePrompt);
  const addMessage = useChatStore((s) => s.addMessage);

  // For NPCs, use the character name as sender (not "The Deep")
  const senderName = character.isNpc ? character.name : (character.playerName || 'Player');

  // Combat/Initiative
  const { setInitiative: setCombatInitiative, getInitiative } = useCombatStore();
  const currentInitiative = getInitiative(character.id);

  // Initiative panel store (the initiative tracker list)
  const setInitiativePanelInit = useInitiativeStore((s) => s.setInitiative);

  // Spell casting state
  const [castingSpell, setCastingSpell] = useState<string | null>(null);
  const [attackRollMode, setAttackRollMode] = useState<'disadvantage' | 'normal' | 'advantage'>('normal');
  const [sharedKey, setSharedKey] = useState<string | null>(null);

  // Spell info cache
  const [spellInfoCache, setSpellInfoCache] = useState<Record<string, {
    attackType?: 'ranged' | 'melee';
    saveType?: { ability: SaveAbility };
    damage?: SpellDamage;
    healing?: SpellHealing;
    isCantrip?: boolean;
  }>>({});

  // Derived values
  const characterLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
  const skillProfs = (character.skillProficiencies ?? []).map((s) => s.toLowerCase());
  const skillExps = (character.skillExpertise ?? []).map((s) => s.toLowerCase());
  const passivePerception = 10 + getModifier(character.abilityScores.wisdom) +
    (skillProfs.includes('perception') ? character.proficiencyBonus : 0) +
    (skillExps.includes('perception') ? character.proficiencyBonus : 0);

  // Preload spell info for known/prepared spells
  useEffect(() => {
    if (!character.spellcasting) return;
    const spells = [...new Set([...character.spellcasting.knownSpells, ...character.spellcasting.preparedSpells])];
    if (spells.length === 0) return;

    let cancelled = false;
    const cache: Record<string, {
      attackType?: 'ranged' | 'melee';
      saveType?: { ability: SaveAbility };
      damage?: SpellDamage;
      healing?: SpellHealing;
      isCantrip?: boolean;
    }> = {};

    Promise.all(
      spells.map(async (spellName) => {
        try {
          const slug = spellName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          const spell = await getSpell(slug);
          const attackType = parseSpellAttackType(spell.desc);
          const saveType = parseSpellSaveType(spell.desc);
          const baseDamage = parseSpellDamage(spell.desc);
          const healing = parseSpellHealing(spell.desc);
          const isCantrip = spell.level_int === 0;

          if (attackType || saveType || baseDamage || healing) {
            cache[spellName] = {
              ...(attackType ? { attackType } : {}),
              ...(saveType ? { saveType } : {}),
              ...(baseDamage ? { damage: isCantrip ? scaleCantripDamage(baseDamage, characterLevel) : baseDamage } : {}),
              ...(healing ? { healing } : {}),
              isCantrip,
            };
          }
        } catch {
          // Spell lookup failed — skip
        }
      })
    ).then(() => {
      if (!cancelled) setSpellInfoCache(cache);
    });

    return () => { cancelled = true; };
  }, [character.spellcasting, characterLevel]);

  const hpPercent = Math.round((character.currentHp / character.maxHp) * 100);
  const badge = getCharacterBadge(character.badgeId, index);
  const accentColor = badge.accent;

  const flashShared = (key: string) => {
    setSharedKey(key);
    setTimeout(() => setSharedKey(null), 1200);
  };

  const shareToChat = (title: string, description: string, source?: string) => {
    const parts = [`**${title}**`];
    if (source) parts.push(`_(${source})_`);
    if (description) parts.push(description);

    addMessage({
      sender: senderName,
      characterName: character.name,
      message: parts.join('\n'),
      type: 'player',
    });
  };

  // Roll dice and post to chat
  const handleRoll = async (
    notation: string,
    label: string,
    rollType: 'normal' | 'advantage' | 'disadvantage' = 'normal',
    rollLabel?: 'save' | 'check' | 'damage' | 'attack' | 'crit' | 'crit-damage' | null
  ) => {
    console.log('[Flyout] handleRoll called:', { notation, label, rollType, rollLabel, isReady });
    if (!isReady) {
      console.log('[Flyout] Dice not ready, aborting');
      return;
    }

    if (rollType !== 'normal' && notation.match(/^\d*d20$/i)) {
      // Roll twice for advantage/disadvantage
      const result1 = await roll(notation);
      const result2 = await roll(notation);

      if (result1 && result2) {
        const val1 = result1.total;
        const val2 = result2.total;
        const chosen = rollType === 'advantage'
          ? Math.max(val1, val2)
          : Math.min(val1, val2);

        const combinedResult = {
          notation: `${notation} (${label})`,
          rolls: result1.rolls,
          total: chosen,
          advDisRolls: [val1, val2] as [number, number],
          discardedValue: rollType === 'advantage' ? Math.min(val1, val2) : Math.max(val1, val2),
        };

        addDiceRoll({
          sender: senderName,
          characterName: character.name,
          notation: `${notation} (${label})`,
          result: combinedResult as any,
          rollType,
          rollLabel,
        });
      }
    } else {
      const result = await roll(notation);
      if (result) {
        addDiceRoll({
          sender: senderName,
          characterName: character.name,
          notation: `${notation} (${label})`,
          result,
          rollType: 'normal',
          rollLabel,
        });
      }
    }
  };

  // Attack roll with advantage/disadvantage support
  const handleAttackRoll = async (attackBonus: number, label: string) => {
    if (!isReady) return;

    const mode = attackRollMode;
    const notationStr = `1d20${attackBonus >= 0 ? '+' : ''}${attackBonus} (${label})`;

    if (mode !== 'normal') {
      const result = await roll('2d20');
      if (!result) return;

      const die1 = result.rolls[0]?.value ?? 0;
      const die2 = result.rolls[1]?.value ?? 0;
      const chosenDie = mode === 'advantage' ? Math.max(die1, die2) : Math.min(die1, die2);
      const discardedDie = mode === 'advantage' ? Math.min(die1, die2) : Math.max(die1, die2);
      const isCrit = chosenDie === 20;
      const total = chosenDie + attackBonus;

      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: notationStr,
        result: {
          notation: `${notationStr} (${mode})${isCrit ? ' - CRITICAL HIT!' : ''}`,
          rolls: [{ sides: 20, value: chosenDie }],
          total,
          advDisRolls: [die1, die2] as [number, number],
          discardedValue: discardedDie,
        },
        rollType: mode,
        rollLabel: isCrit ? 'crit' : 'attack',
      });
    } else {
      const result = await roll('1d20');
      if (!result) return;

      const dieValue = result.rolls[0]?.value ?? 0;
      const isCrit = dieValue === 20;
      const total = result.total + attackBonus;

      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: notationStr,
        result: {
          notation: notationStr,
          rolls: result.rolls,
          total,
        },
        rollLabel: isCrit ? 'crit' : 'attack',
      });
    }
  };

  // Roll initiative: 1d20 + DEX mod, then set in both stores
  const handleInitiativeRoll = async () => {
    if (!isReady) return;

    const dexMod = getModifier(character.abilityScores.dexterity);
    const result = await roll('1d20');

    if (result) {
      const initiativeTotal = result.total + dexMod;

      // Set initiative in combat store (auto-sorts by initiative desc)
      setCombatInitiative(character.id, initiativeTotal, dexMod);

      // Also update the initiative panel store (id format: char-{characterId})
      setInitiativePanelInit(`char-${character.id}`, initiativeTotal);

      // Post to chat
      addDiceRoll({
        sender: senderName,
        characterName: character.name,
        notation: `1d20+${dexMod}`,
        result: {
          ...result,
          notation: `1d20+${dexMod}`,
          total: initiativeTotal,
        },
        rollType: 'normal',
        rollLabel: 'initiative',
      });
    }
  };

  // Spell casting — Open5e lookup, posts spell card to chat
  const handleCastSpell = async (spellName: string) => {
    setCastingSpell(spellName);
    try {
      const slug = spellName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const spell = await getSpell(slug);

      const cachedInfo = spellInfoCache[spellName];
      const spellDamage = cachedInfo?.damage
        ? { dice: cachedInfo.damage.dice, damageType: cachedInfo.damage.damageType, spellName }
        : undefined;

      const abilityMod = character.spellcasting
        ? getModifier(character.abilityScores[character.spellcasting.ability])
        : 0;
      const spellHealing = cachedInfo?.healing
        ? { dice: cachedInfo.healing.dice, spellName, abilityMod: cachedInfo.healing.addAbilityMod ? abilityMod : 0 }
        : undefined;

      addSpellLookup({ sender: senderName, spell, spellDamage, spellHealing });
    } catch (error) {
      console.error(`Failed to look up spell "${spellName}":`, error);
      addMessage({
        sender: senderName,
        characterName: character.name,
        message: `*casts **${spellName}***`,
        type: 'player',
      });
    } finally {
      setCastingSpell(null);
    }
  };

  // Spell attack — post spell card then roll attack
  const handleSpellAttack = async (spellName: string) => {
    if (!isReady || !character.spellcasting) return;
    await handleCastSpell(spellName);
    const abilityMod = getModifier(character.abilityScores[character.spellcasting.ability]);
    const spellAttackBonus = abilityMod + character.proficiencyBonus;
    await handleAttackRoll(spellAttackBonus, `${spellName} (Spell Attack)`);
  };

  // Save-based spell — post spell card then save prompt
  const handleSpellSave = async (spellName: string, saveAbility: string) => {
    if (!character.spellcasting) return;
    await handleCastSpell(spellName);
    const abilityMod = getModifier(character.abilityScores[character.spellcasting.ability]);
    const saveDC = 8 + abilityMod + character.proficiencyBonus;
    addSavePrompt({ casterName: character.name, spellName, dc: saveDC, ability: saveAbility });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 40,
        left: 290,
        width: 400,
        maxHeight: 'calc(100vh - 60px)',
        backgroundColor: '#0a080c', // Deep Melnibonéan black
        borderRadius: '2px',
        border: `1px solid ${accentColor}40`,
        boxShadow: `0 0 60px rgba(0, 0, 0, 0.8), 0 0 20px ${accentColor}15, inset 0 0 80px rgba(0,0,0,0.5)`,
        zIndex: 1000,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Chaos corner flourishes */}
      <CornerFlourish position="top-left" color={accentColor} />
      <CornerFlourish position="top-right" color={accentColor} />

      {/* Runic borders */}
      <RunicBorder color={accentColor} vertical />

      {/* Header - dark and atmospheric */}
      <div
        style={{
          position: 'relative',
          padding: '28px 24px 24px',
          color: 'white',
          overflow: 'hidden',
          flexShrink: 0,
          zIndex: 2,
        }}
      >
        {/* Subtle gradient with badge color tint */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, ${accentColor}15 0%, transparent 60%, rgba(0,0,0,0.4) 100%)`,
          }}
        />
        {/* Chaos star watermark */}
        <div style={{ position: 'absolute', top: 10, right: 20, opacity: 0.08 }}>
          <ChaosStar size={80} color="#fff" opacity={1} />
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {/* Name in elegant serif */}
              <h2 style={{
                margin: 0,
                fontSize: 28,
                fontWeight: 300,
                fontFamily: 'Georgia, serif',
                letterSpacing: '2px',
                textShadow: `0 0 20px ${accentColor}60`,
                color: '#f0e8f8',
              }}>
                {character.name}
              </h2>
              <div style={{
                fontSize: 13,
                marginTop: 6,
                color: 'rgba(180, 160, 200, 0.9)',
                fontFamily: 'serif',
                fontStyle: 'italic',
                letterSpacing: '0.5px',
              }}>
                {character.classes.map(c => `${c.name} ${c.level}`).join(' / ')} · {character.race}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {/* Change badge */}
              <button
                onClick={() => setShowBadgePicker(true)}
                title="Change badge color"
                style={{
                  background: badge.color,
                  border: `2px solid ${badge.accent}`,
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: `0 0 4px ${badge.color}40`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = `0 0 10px ${badge.accent}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = `0 0 4px ${badge.color}40`;
                }}
              />
              {/* Pop out to floating window */}
              <button
                onClick={() => {
                  openCharacterSheet(character.id);
                  onClose();
                }}
                title="Pop out to window"
                style={{
                  background: 'transparent',
                  border: `1px solid ${accentColor}40`,
                  borderRadius: '2px',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  color: 'rgba(180, 160, 200, 0.8)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = accentColor;
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.boxShadow = `0 0 10px ${accentColor}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${accentColor}40`;
                  e.currentTarget.style.color = 'rgba(180, 160, 200, 0.8)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Icon name="expand" size={14} />
              </button>
              {/* Close flyout */}
              <button
                onClick={onClose}
                title="Close"
                style={{
                  background: 'transparent',
                  border: `1px solid ${accentColor}40`,
                  borderRadius: '2px',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  color: 'rgba(180, 160, 200, 0.8)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = accentColor;
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.boxShadow = `0 0 10px ${accentColor}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${accentColor}40`;
                  e.currentTarget.style.color = 'rgba(180, 160, 200, 0.8)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Icon name="minus" size={14} />
              </button>
            </div>
          </div>

          {/* Class badges - dark and elegant */}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            {character.classes.map((c: { name: string; subclass?: string; level: number }, i: number) => (
              <div
                key={i}
                style={{
                  padding: '5px 12px',
                  backgroundColor: 'rgba(0,0,0,0.4)',
                  borderRadius: '2px',
                  fontSize: 11,
                  fontWeight: 400,
                  fontFamily: 'serif',
                  border: `1px solid ${accentColor}30`,
                  color: 'rgba(200, 180, 220, 0.9)',
                  letterSpacing: '0.5px',
                }}
              >
                <Icon name={getClassIcon(c.name)} size={10} color={accentColor} style={{ marginRight: 6 }} />
                {c.name} {c.level}{c.subclass && <span style={{ opacity: 0.7 }}> · {c.subclass}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade line */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 20,
          right: 20,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accentColor}40 30%, ${accentColor}40 70%, transparent)`,
        }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 100%)' }}>
        {/* Quick Rolls Section */}
        <ParchmentSection title="Quick Rolls" icon="dice" accentColor={accentColor}>
          {/* Initiative button - only show if not rolled yet */}
          {currentInitiative === null && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={handleInitiativeRoll}
                disabled={!isReady}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.3) 0%, rgba(160, 128, 48, 0.4) 100%)',
                  color: '#d4af37',
                  border: '1px solid rgba(212, 175, 55, 0.5)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  opacity: isReady ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <span>⚔️</span>
                <span>Roll Initiative (+{getModifier(character.abilityScores.dexterity)})</span>
              </button>
            </div>
          )}

          {/* Rest Buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => {
                // Short rest: restore hit dice (up to half level), abilities that recharge on short rest
                const hitDiceToRestore = Math.min(
                  Math.floor(character.classes.reduce((sum, c) => sum + c.level, 0) / 2),
                  character.classes.reduce((sum, c) => sum + c.level, 0) - character.hitDiceRemaining
                );
                if (hitDiceToRestore > 0) {
                  updateCharacter(character.id, { hitDiceRemaining: character.hitDiceRemaining + hitDiceToRestore });
                }
                // TODO: Chat message about short rest
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'linear-gradient(180deg, rgba(70, 130, 180, 0.3) 0%, rgba(40, 80, 120, 0.4) 100%)',
                color: '#87ceeb',
                border: '1px solid rgba(70, 130, 180, 0.5)',
                borderRadius: '2px',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.5px',
              }}
            >
              🌙 Short Rest
            </button>
            <button
              onClick={() => {
                // Long rest: full HP, restore all hit dice, all spell slots
                const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
                updateCharacter(character.id, {
                  currentHp: character.maxHp,
                  tempHp: 0,
                  hitDiceRemaining: totalLevel,
                  spellcasting: character.spellcasting ? {
                    ...character.spellcasting,
                    spellSlotsUsed: {},
                  } : undefined,
                });
                // TODO: Chat message about long rest
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'linear-gradient(180deg, rgba(75, 0, 130, 0.3) 0%, rgba(40, 0, 70, 0.4) 100%)',
                color: '#dda0dd',
                border: '1px solid rgba(138, 43, 226, 0.5)',
                borderRadius: '2px',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.5px',
              }}
            >
              ⭐ Long Rest
            </button>
          </div>

          {/* Combat Actions — always available */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 6,
            }}>
              <div style={{
                flex: 1,
                fontSize: 10,
                color: 'rgba(212, 175, 55, 0.8)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <span style={{ color: '#d4af37' }}>⚔</span> Attacks
              </div>
              {/* DIS/—/ADV toggle */}
              <div style={{ display: 'flex', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 3, overflow: 'hidden' }}>
                {(['disadvantage', 'normal', 'advantage'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setAttackRollMode(mode)}
                    style={{
                      padding: '3px 8px',
                      fontSize: 9,
                      fontWeight: attackRollMode === mode ? 600 : 400,
                      backgroundColor:
                        attackRollMode === mode
                          ? mode === 'advantage' ? '#22c55e'
                          : mode === 'disadvantage' ? '#dc143c'
                          : 'rgba(192,192,192,0.5)'
                          : 'transparent',
                      color: attackRollMode === mode ? '#0a080c' : 'rgba(160, 150, 180, 0.6)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {mode === 'disadvantage' ? 'DIS' : mode === 'advantage' ? 'ADV' : '—'}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => {
                  const strMod = getModifier(character.abilityScores.strength);
                  const attackBonus = strMod + character.proficiencyBonus;
                  handleAttackRoll(attackBonus, 'Melee Attack');
                }}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.5) 0%, rgba(80, 0, 0, 0.7) 100%)',
                  color: '#ffd700',
                  border: '1px solid rgba(212, 175, 55, 0.5)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '0.5px',
                  opacity: isReady ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name="sword" size={12} color="#ffd700" />
                Melee +{getModifier(character.abilityScores.strength) + character.proficiencyBonus}
              </button>
              <button
                onClick={() => {
                  const dexMod = getModifier(character.abilityScores.dexterity);
                  const attackBonus = dexMod + character.proficiencyBonus;
                  handleAttackRoll(attackBonus, 'Ranged Attack');
                }}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  background: 'linear-gradient(180deg, rgba(0, 80, 60, 0.5) 0%, rgba(0, 50, 40, 0.7) 100%)',
                  color: '#90ee90',
                  border: '1px solid rgba(144, 238, 144, 0.4)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '0.5px',
                  opacity: isReady ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name="target" size={12} color="#90ee90" />
                Ranged +{getModifier(character.abilityScores.dexterity) + character.proficiencyBonus}
              </button>
            </div>
          </div>

          {/* d20 row with adv/dis */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 10,
              color: 'rgba(160, 150, 180, 0.6)',
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}>
              D20 Roll
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => handleRoll('1d20', 'd20', 'disadvantage')}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.4) 0%, rgba(74, 0, 0, 0.6) 100%)',
                  color: 'rgba(255, 180, 180, 0.9)',
                  border: '1px solid rgba(139, 0, 0, 0.4)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 10,
                  fontWeight: 600,
                  opacity: isReady ? 1 : 0.5,
                }}
              >
                DIS
              </button>
              <button
                onClick={() => handleRoll('1d20', 'd20')}
                disabled={!isReady}
                style={{
                  flex: 2,
                  padding: '10px 8px',
                  background: 'linear-gradient(180deg, rgba(100, 80, 120, 0.5) 0%, rgba(60, 50, 80, 0.7) 100%)',
                  color: '#e8e4f0',
                  border: `1px solid ${accentColor}60`,
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'Georgia, serif',
                  opacity: isReady ? 1 : 0.5,
                }}
              >
                🎲 1d20
              </button>
              <button
                onClick={() => handleRoll('1d20', 'd20', 'advantage')}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(0, 100, 50, 0.4) 0%, rgba(0, 60, 30, 0.6) 100%)',
                  color: 'rgba(180, 255, 200, 0.9)',
                  border: '1px solid rgba(0, 139, 69, 0.4)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 10,
                  fontWeight: 600,
                  opacity: isReady ? 1 : 0.5,
                }}
              >
                ADV
              </button>
            </div>
          </div>

          {/* Saving throws */}
          <div style={{
            fontSize: 10,
            color: 'rgba(160, 150, 180, 0.6)',
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            Saving Throws
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
            {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map((ability) => {
              const mod = getModifier(character.abilityScores[ability]);
              const isProficient = character.savingThrowProficiencies.includes(ability);
              const bonus = mod + (isProficient ? character.proficiencyBonus : 0);
              const bonusStr = bonus >= 0 ? `+${bonus}` : `${bonus}`;

              return (
                <button
                  key={ability}
                  onClick={() => handleRoll(`1d20${bonusStr}`, `${ability.slice(0, 3).toUpperCase()} Save`, 'normal', 'save')}
                  disabled={!isReady}
                  style={{
                    flex: '1 1 30%',
                    padding: '6px 4px',
                    background: isProficient
                      ? 'linear-gradient(180deg, rgba(192, 192, 192, 0.2) 0%, rgba(128, 128, 128, 0.3) 100%)'
                      : 'rgba(0, 0, 0, 0.3)',
                    color: isProficient ? '#d4d4d8' : 'rgba(160, 150, 180, 0.7)',
                    border: isProficient ? '1px solid rgba(192, 192, 192, 0.3)' : '1px solid rgba(100, 80, 120, 0.2)',
                    borderRadius: '2px',
                    cursor: isReady ? 'pointer' : 'not-allowed',
                    fontSize: 9,
                    fontFamily: 'var(--font-mono)',
                    opacity: isReady ? 1 : 0.5,
                  }}
                >
                  {ability.slice(0, 3).toUpperCase()} {bonusStr}
                </button>
              );
            })}
          </div>

          {/* Common damage dice */}
          <div style={{
            fontSize: 10,
            color: 'rgba(160, 150, 180, 0.6)',
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            Damage Dice
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['1d4', '1d6', '1d8', '1d10', '1d12', '2d6'].map((die) => (
              <button
                key={die}
                onClick={() => handleRoll(die, die, 'normal', 'damage')}
                disabled={!isReady}
                style={{
                  flex: 1,
                  padding: '6px 2px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: 'rgba(180, 160, 200, 0.8)',
                  border: '1px solid rgba(100, 80, 120, 0.2)',
                  borderRadius: '2px',
                  cursor: isReady ? 'pointer' : 'not-allowed',
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  opacity: isReady ? 1 : 0.5,
                }}
              >
                {die}
              </button>
            ))}
          </div>
        </ParchmentSection>

        {/* HP Section - Stormbringer blood theme */}
        <ParchmentSection title="Vitality" icon="heart" accentColor={accentColor}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <div style={{
              fontSize: 32,
              fontWeight: 300,
              fontFamily: 'Georgia, serif',
              color: '#e8e4f0',
              textShadow: '0 0 10px rgba(200, 180, 220, 0.3)',
            }}>
              {character.currentHp}
              <span style={{
                fontSize: 16,
                color: 'rgba(160, 140, 180, 0.7)',
                fontWeight: 300,
              }}> / {character.maxHp}</span>
            </div>
            {character.tempHp > 0 && (
              <div style={{
                padding: '3px 10px',
                background: 'linear-gradient(135deg, rgba(100, 200, 255, 0.2) 0%, rgba(100, 200, 255, 0.1) 100%)',
                border: '1px solid rgba(100, 200, 255, 0.3)',
                borderRadius: '2px',
                fontSize: 11,
                fontFamily: 'serif',
                color: 'rgba(150, 220, 255, 0.9)',
              }}>
                +{character.tempHp} ephemeral
              </div>
            )}
          </div>

          {/* HP Bar - blood/silver gradient */}
          <div
            style={{
              height: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '1px',
              overflow: 'hidden',
              marginBottom: 16,
              border: '1px solid rgba(100, 80, 120, 0.2)',
            }}
          >
            <div
              style={{
                width: `${hpPercent}%`,
                height: '100%',
                background: hpPercent >= 50
                  ? 'linear-gradient(90deg, #c0c0c0, #e8e8e8)' // Silver - healthy
                  : hpPercent >= 25
                    ? 'linear-gradient(90deg, #8b0000, #dc143c)' // Blood red - wounded
                    : 'linear-gradient(90deg, #4a0000, #8b0000)', // Dark blood - critical
                transition: 'width 0.4s ease',
                boxShadow: hpPercent >= 50 ? '0 0 8px rgba(200, 200, 200, 0.5)' : '0 0 8px rgba(139, 0, 0, 0.5)',
              }}
            />
          </div>

          {/* HP Buttons - dark elegant style */}
          <div style={{ display: 'flex', gap: 6 }}>
            {[-10, -5, -1].map((amt) => (
              <button
                key={amt}
                onClick={() => damageCharacter(character.id, Math.abs(amt))}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.6) 0%, rgba(74, 0, 0, 0.8) 100%)',
                  color: 'rgba(255, 200, 200, 0.9)',
                  border: '1px solid rgba(139, 0, 0, 0.4)',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  transition: 'all 0.2s',
                }}
              >
                {amt}
              </button>
            ))}
            <div style={{ width: 8 }} />
            {[1, 5, 10].map((amt) => (
              <button
                key={amt}
                onClick={() => healCharacter(character.id, amt)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: 'linear-gradient(180deg, rgba(192, 192, 192, 0.3) 0%, rgba(128, 128, 128, 0.4) 100%)',
                  color: 'rgba(220, 220, 230, 0.9)',
                  border: '1px solid rgba(192, 192, 192, 0.3)',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                  transition: 'all 0.2s',
                }}
              >
                +{amt}
              </button>
            ))}
          </div>
        </ParchmentSection>

        {/* Combat Stats - Silver medallions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '20px 0',
          marginBottom: 8,
        }}>
          {[
            { label: 'Armour', value: character.armorClass, icon: 'shield' as IconName },
            { label: 'Movement', value: `${character.speed}'`, icon: 'target' as IconName },
            { label: 'Mastery', value: `+${character.proficiencyBonus}`, icon: 'star' as IconName },
            { label: 'Perception', value: passivePerception, icon: 'eye' as IconName },
          ].map((stat) => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div style={{
                width: 52,
                height: 52,
                margin: '0 auto 10px',
                borderRadius: '50%',
                border: '1px solid rgba(192, 192, 192, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 30% 30%, rgba(60, 50, 70, 0.8) 0%, rgba(20, 15, 25, 0.9) 100%)',
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.5), 0 0 15px rgba(192, 192, 192, 0.1)',
              }}>
                <div>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 300,
                    fontFamily: 'Georgia, serif',
                    color: '#d4d4d8',
                    textShadow: '0 0 8px rgba(192, 192, 192, 0.4)',
                  }}>
                    {stat.value}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: 9,
                color: 'rgba(160, 150, 180, 0.7)',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                fontFamily: 'serif',
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        <SectionDivider color="#c0c0c0" />

        {/* Ability Scores */}
        <ParchmentSection title="Attributes" icon="crystal" accentColor={accentColor}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map((ability) => (
              <AbilityShield
                key={ability}
                ability={ability}
                score={character.abilityScores[ability]}
                modifier={getModifier(character.abilityScores[ability])}
                isProficient={character.savingThrowProficiencies.includes(ability)}
                accentColor="#c0c0c0" // Silver accent for proficiencies
              />
            ))}
          </div>
        </ParchmentSection>

        {/* Skills */}
        <ParchmentSection title="Skills" icon="star" accentColor={accentColor}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px 12px' }}>
            {SKILLS.map((skill) => {
              const mod = getModifier(character.abilityScores[skill.ability]);
              const isProficient = skillProfs.includes(skill.name.toLowerCase());
              const isExpert = skillExps.includes(skill.name.toLowerCase());
              const profBonus = isProficient ? character.proficiencyBonus : 0;
              const expertBonus = isExpert ? character.proficiencyBonus : 0;
              const total = mod + profBonus + expertBonus;
              const notation = total >= 0 ? `1d20+${total}` : `1d20${total}`;

              return (
                <button
                  key={skill.name}
                  onClick={() => handleRoll(notation, skill.name, 'normal', 'check')}
                  disabled={!isReady}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 6px',
                    background: isProficient ? 'rgba(255,255,255,0.05)' : 'transparent',
                    borderRadius: '2px',
                    border: 'none',
                    cursor: isReady ? 'pointer' : 'not-allowed',
                    width: '100%',
                    textAlign: 'left',
                    opacity: isReady ? 1 : 0.5,
                  }}
                  title={`Roll ${skill.name} check (${formatModifier(total)})`}
                >
                  <div style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: isExpert ? accentColor : isProficient ? 'rgba(255,255,255,0.5)' : 'transparent',
                    border: `1px solid ${isProficient ? 'rgba(255,255,255,0.5)' : 'rgba(100,80,120,0.3)'}`,
                    flexShrink: 0,
                  }} />
                  <span style={{
                    flex: 1,
                    fontSize: 10,
                    color: isProficient ? '#d4d4d8' : 'rgba(160, 150, 180, 0.7)',
                  }}>
                    {skill.name}
                  </span>
                  <span style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    color: total >= 0 ? 'rgba(134, 239, 172, 0.9)' : 'rgba(248, 113, 113, 0.9)',
                  }}>
                    {formatModifier(total)}
                  </span>
                </button>
              );
            })}
          </div>
        </ParchmentSection>

        {/* Spellcasting */}
        {character.spellcasting && (
          <ParchmentSection title="Spellcasting" icon="wand" accentColor={accentColor}>
            <div style={{ marginBottom: 12 }}>
              {(() => {
                const abilityMod = getModifier(character.abilityScores[character.spellcasting!.ability]);
                const spellAttackBonus = abilityMod + character.proficiencyBonus;
                const saveDC = 8 + abilityMod + character.proficiencyBonus;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, fontSize: 10, color: 'rgba(160, 150, 180, 0.7)' }}>
                    <span style={{ textTransform: 'capitalize' }}>{character.spellcasting!.ability}</span>
                    <span>Atk: <span style={{ color: '#90ee90', fontFamily: 'var(--font-mono)' }}>{formatModifier(spellAttackBonus)}</span></span>
                    <span>DC: <span style={{ color: '#ffd700', fontFamily: 'var(--font-mono)' }}>{saveDC}</span></span>
                  </div>
                );
              })()}

              {/* Spell Slots */}
              {Object.keys(character.spellcasting.spellSlots).length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Object.entries(character.spellcasting.spellSlots).map(([level, total]) => {
                      const used = character.spellcasting?.spellSlotsUsed[level] || 0;
                      const remaining = total - used;
                      return (
                        <div key={level} style={{
                          padding: '4px 8px',
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: '2px',
                          textAlign: 'center',
                        }}>
                          <div style={{ fontSize: 8, color: 'rgba(160,150,180,0.6)' }}>LV {level}</div>
                          <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: remaining > 0 ? '#d4d4d8' : 'rgba(160,150,180,0.4)' }}>{remaining}/{total}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cantrips */}
              {character.spellcasting.knownSpells.length > 0 && character.spellcasting.preparedSpells.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: 'rgba(160,150,180,0.6)', marginBottom: 4 }}>Cantrips</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {character.spellcasting.knownSpells.map((spellName, i) => {
                      const isCasting = castingSpell === spellName;
                      const spellInfo = spellInfoCache[spellName];
                      const attackType = spellInfo?.attackType;
                      const saveType = spellInfo?.saveType;
                      const abilityMod = character.spellcasting ? getModifier(character.abilityScores[character.spellcasting.ability]) : 0;
                      const spellAttackBonus = abilityMod + character.proficiencyBonus;
                      const saveDC = 8 + abilityMod + character.proficiencyBonus;

                      if (attackType) {
                        const isRanged = attackType === 'ranged';
                        const attackColor = isRanged ? 'rgba(0, 100, 100, 0.7)' : 'rgba(120, 20, 20, 0.7)';
                        const attackTextColor = isRanged ? '#7dd3d3' : '#e8a0a0';
                        const attackIcon: IconName = isRanged ? 'target' : 'sword';
                        return (
                          <div key={`cantrip-${i}`} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRight: 'none', borderRadius: '2px 0 0 2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                              <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                            </button>
                            <button onClick={() => handleSpellAttack(spellName)} disabled={isCasting || !isReady} style={{ padding: '4px 6px', background: attackColor, border: `1px solid ${attackTextColor}60`, borderRadius: '0 2px 2px 0', fontSize: 10, fontFamily: 'var(--font-mono)', cursor: isCasting || !isReady ? 'not-allowed' : 'pointer', color: attackTextColor, display: 'flex', alignItems: 'center', gap: 3, opacity: isCasting || !isReady ? 0.5 : 1 }} title={`${isRanged ? 'Ranged' : 'Melee'} spell attack ${formatModifier(spellAttackBonus)}`}>
                              <Icon name={attackIcon} size={10} color={attackTextColor} />{formatModifier(spellAttackBonus)}
                            </button>
                          </div>
                        );
                      }

                      if (saveType) {
                        const abilityShort = saveType.ability.slice(0, 3).toUpperCase();
                        return (
                          <div key={`cantrip-${i}`} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRight: 'none', borderRadius: '2px 0 0 2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                              <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                            </button>
                            <button onClick={() => handleSpellSave(spellName, saveType.ability)} disabled={isCasting} style={{ padding: '4px 6px', background: 'rgba(180, 140, 20, 0.5)', border: '1px solid rgba(212, 175, 55, 0.5)', borderRadius: '0 2px 2px 0', fontSize: 9, fontFamily: 'var(--font-mono)', cursor: isCasting ? 'not-allowed' : 'pointer', color: '#ffd700', display: 'flex', alignItems: 'center', gap: 3, opacity: isCasting ? 0.5 : 1 }} title={`${spellName} — ${abilityShort} Save DC ${saveDC}`}>
                              <Icon name="shield" size={10} color="#ffd700" />DC {saveDC} {abilityShort}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <button key={`cantrip-${i}`} onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRadius: '2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                          <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prepared Spells */}
              {(character.spellcasting.knownSpells.length > 0 || character.spellcasting.preparedSpells.length > 0) && (
                <div>
                  <div style={{ fontSize: 10, color: 'rgba(160,150,180,0.6)', marginBottom: 4 }}>
                    {character.spellcasting.preparedSpells.length > 0 ? 'Prepared Spells' : 'Known Spells'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(character.spellcasting.preparedSpells.length > 0
                      ? character.spellcasting.preparedSpells
                      : character.spellcasting.knownSpells
                    ).map((spellName, i) => {
                      const isCasting = castingSpell === spellName;
                      const spellInfo = spellInfoCache[spellName];
                      const attackType = spellInfo?.attackType;
                      const saveType = spellInfo?.saveType;
                      const abilityMod = character.spellcasting ? getModifier(character.abilityScores[character.spellcasting.ability]) : 0;
                      const spellAttackBonus = abilityMod + character.proficiencyBonus;
                      const saveDC = 8 + abilityMod + character.proficiencyBonus;

                      if (attackType) {
                        const isRanged = attackType === 'ranged';
                        const attackColor = isRanged ? 'rgba(0, 100, 100, 0.7)' : 'rgba(120, 20, 20, 0.7)';
                        const attackTextColor = isRanged ? '#7dd3d3' : '#e8a0a0';
                        const attackIcon: IconName = isRanged ? 'target' : 'sword';
                        return (
                          <div key={i} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRight: 'none', borderRadius: '2px 0 0 2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                              <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                            </button>
                            <button onClick={() => handleSpellAttack(spellName)} disabled={isCasting || !isReady} style={{ padding: '4px 6px', background: attackColor, border: `1px solid ${attackTextColor}60`, borderRadius: '0 2px 2px 0', fontSize: 10, fontFamily: 'var(--font-mono)', cursor: isCasting || !isReady ? 'not-allowed' : 'pointer', color: attackTextColor, display: 'flex', alignItems: 'center', gap: 3, opacity: isCasting || !isReady ? 0.5 : 1 }} title={`${isRanged ? 'Ranged' : 'Melee'} spell attack ${formatModifier(spellAttackBonus)}`}>
                              <Icon name={attackIcon} size={10} color={attackTextColor} />{formatModifier(spellAttackBonus)}
                            </button>
                          </div>
                        );
                      }

                      if (saveType) {
                        const abilityShort = saveType.ability.slice(0, 3).toUpperCase();
                        return (
                          <div key={i} style={{ display: 'flex', borderRadius: '2px', overflow: 'hidden' }}>
                            <button onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRight: 'none', borderRadius: '2px 0 0 2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                              <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                            </button>
                            <button onClick={() => handleSpellSave(spellName, saveType.ability)} disabled={isCasting} style={{ padding: '4px 6px', background: 'rgba(180, 140, 20, 0.5)', border: '1px solid rgba(212, 175, 55, 0.5)', borderRadius: '0 2px 2px 0', fontSize: 9, fontFamily: 'var(--font-mono)', cursor: isCasting ? 'not-allowed' : 'pointer', color: '#ffd700', display: 'flex', alignItems: 'center', gap: 3, opacity: isCasting ? 0.5 : 1 }} title={`${spellName} — ${abilityShort} Save DC ${saveDC}`}>
                              <Icon name="shield" size={10} color="#ffd700" />DC {saveDC} {abilityShort}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <button key={i} onClick={() => handleCastSpell(spellName)} disabled={isCasting} style={{ padding: '4px 8px', background: isCasting ? `${accentColor}40` : `${accentColor}20`, border: `1px solid ${accentColor}50`, borderRadius: '2px', fontSize: 10, cursor: isCasting ? 'wait' : 'pointer', color: '#d4d4d8', display: 'flex', alignItems: 'center', gap: 4 }} title={`Cast ${spellName}`}>
                          <Icon name="wand" size={10} color={accentColor} />{spellName}{isCasting && <span>...</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </ParchmentSection>
        )}

        {/* Features — all shown, clickable to share */}
        {character.features.length > 0 && (
          <ParchmentSection title="Powers & Abilities" icon="sparkle" accentColor={accentColor}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {character.features.map((feature: { name: string; source: string; description: string }, i: number) => {
                const key = `feature-${i}`;
                const isShared = sharedKey === key;
                return (
                  <div
                    key={i}
                    onClick={() => {
                      shareToChat(feature.name, feature.description, feature.source);
                      flashShared(key);
                    }}
                    style={{
                      padding: '10px 12px',
                      background: isShared ? `${accentColor}25` : 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '2px',
                      borderLeft: `2px solid ${isShared ? accentColor : 'rgba(192, 192, 192, 0.4)'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 4,
                    }}>
                      <span style={{
                        fontWeight: 500,
                        fontSize: 12,
                        color: '#d4d4d8',
                        fontFamily: 'Georgia, serif',
                        letterSpacing: '0.5px',
                      }}>
                        {feature.name}
                      </span>
                      <span style={{ fontSize: 9, color: isShared ? '#d4af37' : 'rgba(140, 130, 160, 0.6)', fontStyle: 'italic' }}>
                        {isShared ? 'Shared!' : feature.source}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: 'rgba(160, 150, 180, 0.8)',
                      lineHeight: 1.5,
                      fontStyle: 'italic',
                    }}>
                      {feature.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </ParchmentSection>
        )}

        {/* Resources */}
        {(character.resources ?? []).length > 0 && (
          <ParchmentSection title="Resources" icon="crystal" accentColor={accentColor}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(character.resources ?? []).map((resource, i) => {
                const key = `resource-${i}`;
                const isShared = sharedKey === key;
                return (
                  <div
                    key={i}
                    onClick={() => {
                      shareToChat(resource.name, `${resource.current}/${resource.max} remaining (recharges: ${resource.rechargeOn.replace('_', ' ')})`);
                      flashShared(key);
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 10px',
                      background: isShared ? `${accentColor}25` : 'rgba(0,0,0,0.3)',
                      borderRadius: '2px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    <div>
                      <div style={{ fontSize: 11, color: '#d4d4d8' }}>{resource.name}</div>
                      <div style={{ fontSize: 9, color: isShared ? '#d4af37' : 'rgba(160,150,180,0.6)', textTransform: 'capitalize' }}>
                        {isShared ? 'Shared!' : `Recharges: ${resource.rechargeOn.replace('_', ' ')}`}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: '#d4d4d8' }}>
                      {resource.current}/{resource.max}
                    </div>
                  </div>
                );
              })}
            </div>
          </ParchmentSection>
        )}

        {/* Equipment */}
        {(character.equipment ?? []).length > 0 && (
          <ParchmentSection title="Equipment" icon="shield" accentColor={accentColor}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(character.equipment ?? []).map((item, i) => {
                const key = `equip-${i}`;
                const isShared = sharedKey === key;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      shareToChat(item, '', 'Equipment');
                      flashShared(key);
                    }}
                    style={{
                      padding: '4px 8px',
                      background: isShared ? `${accentColor}25` : 'rgba(0,0,0,0.3)',
                      border: `1px solid ${isShared ? accentColor : 'rgba(100,80,120,0.2)'}`,
                      borderRadius: '2px',
                      fontSize: 10,
                      cursor: 'pointer',
                      color: '#d4d4d8',
                      transition: 'all 0.2s',
                    }}
                    title="Click to share in team chat"
                  >
                    {isShared ? `${item} ✓` : item}
                  </button>
                );
              })}
            </div>
          </ParchmentSection>
        )}

        {/* NPC Controls - only for NPC characters */}
        {character.isNpc && (
          <NpcControlSection character={character} accentColor={accentColor} />
        )}

        {/* View Full Sheet Button */}
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <button
            onClick={() => {
              openCharacterSheet(character.id);
              onClose();
            }}
            style={{
              background: `linear-gradient(180deg, ${accentColor}30 0%, ${accentColor}20 100%)`,
              border: `1px solid ${accentColor}60`,
              borderRadius: 4,
              padding: '10px 24px',
              cursor: 'pointer',
              color: '#fff',
              fontSize: 13,
              fontFamily: 'var(--font-display)',
              letterSpacing: '0.5px',
              transition: 'all 0.2s',
              width: '100%',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `linear-gradient(180deg, ${accentColor}50 0%, ${accentColor}30 100%)`;
              e.currentTarget.style.borderColor = accentColor;
              e.currentTarget.style.boxShadow = `0 0 15px ${accentColor}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = `linear-gradient(180deg, ${accentColor}30 0%, ${accentColor}20 100%)`;
              e.currentTarget.style.borderColor = `${accentColor}60`;
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            View Full Sheet
          </button>
        </div>

        {/* Remove from Party - GM only */}
        {isGM && (
          <div style={{ padding: '0 16px 16px', textAlign: 'center' }}>
            {!confirmRemove ? (
              <button
                onClick={() => setConfirmRemove(true)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(139, 0, 0, 0.3)',
                  borderRadius: 4,
                  padding: '8px 24px',
                  cursor: 'pointer',
                  color: 'rgba(200, 100, 100, 0.7)',
                  fontSize: 11,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 0, 0, 0.6)';
                  e.currentTarget.style.color = '#dc143c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 0, 0, 0.3)';
                  e.currentTarget.style.color = 'rgba(200, 100, 100, 0.7)';
                }}
              >
                Remove from Party
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    deleteFromSupabase(character.id);
                    removeCharacter(character.id);
                    onClose();
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: 'linear-gradient(180deg, rgba(139, 0, 0, 0.5) 0%, rgba(80, 0, 0, 0.7) 100%)',
                    border: '1px solid rgba(220, 20, 60, 0.5)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: '#ff6b6b',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  Confirm Remove
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(192, 192, 192, 0.3)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: 'rgba(192, 192, 192, 0.8)',
                    fontSize: 11,
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer - Player/NPC info */}
        {(character.playerName || character.isNpc) && (
          <div
            style={{
              padding: '14px 16px',
              textAlign: 'center',
              fontSize: 11,
              color: 'rgba(140, 130, 160, 0.6)',
              borderTop: '1px solid rgba(100, 80, 120, 0.15)',
              fontFamily: 'serif',
              fontStyle: 'italic',
            }}
          >
            {character.isNpc ? (
              <>Controlled by <span style={{ color: character.npcControlMode === 'ai' ? '#a855f7' : 'rgba(200, 190, 220, 0.8)', fontStyle: 'normal' }}>
                {character.npcControlMode === 'ai' ? 'The Deep' : character.npcControlMode === 'player' ? (character.playerName || 'Player') : 'Game Master'}
              </span></>
            ) : (
              <>Guided by <span style={{ color: 'rgba(200, 190, 220, 0.8)', fontStyle: 'normal' }}>{character.playerName}</span></>
            )}
          </div>
        )}
      </div>

      {/* Bottom flourishes */}
      <CornerFlourish position="bottom-left" color={accentColor} />
      <CornerFlourish position="bottom-right" color={accentColor} />

      {/* Badge picker modal */}
      {showBadgePicker && (
        <BadgePicker
          currentBadgeId={character.badgeId}
          currentBadgeIcon={character.badgeIcon}
          onSelectColor={(badgeId) => {
            setBadge(character.id, badgeId);
          }}
          onSelectIcon={(badgeIcon) => {
            setBadgeIcon(character.id, badgeIcon);
          }}
          onClose={() => setShowBadgePicker(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// NPC CONTROL SECTION (within flyout)
// =============================================================================

function NpcControlSection({ character, accentColor }: { character: Character; accentColor: string }) {
  const updateCharacter = useCharacterStore((s) => s.updateCharacter);
  const npcs = useNpcStore((s) => s.npcs);

  const linkedNpc = character.npcCampaignNpcId
    ? npcs.find((n) => n.id === character.npcCampaignNpcId)
    : null;

  const controlModes = ['gm', 'ai', 'player'] as const;
  const controlLabels: Record<string, string> = { gm: 'GM', ai: 'AI', player: 'Player' };

  return (
    <ParchmentSection title="NPC Control" icon="crown" accentColor={accentColor}>
      {/* Linked Campaign NPC */}
      {linkedNpc && (
        <div style={{
          padding: '8px 10px',
          marginBottom: 12,
          backgroundColor: 'rgba(212, 175, 55, 0.08)',
          borderRadius: 3,
          border: '1px solid rgba(212, 175, 55, 0.2)',
          fontSize: 11,
          color: 'rgba(200, 190, 220, 0.9)',
        }}>
          <span style={{ color: '#d4af37', fontWeight: 500 }}>{linkedNpc.name}</span>
          {linkedNpc.title && <span style={{ color: 'rgba(160, 150, 180, 0.7)' }}> — {linkedNpc.title}</span>}
        </div>
      )}

      {/* Control Mode Selector */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 10,
          color: 'rgba(160, 150, 180, 0.6)',
          marginBottom: 6,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Control Mode
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {controlModes.map((mode) => (
            <button
              key={mode}
              onClick={() => updateCharacter(character.id, { npcControlMode: mode })}
              style={{
                flex: 1,
                padding: '6px 8px',
                fontSize: 10,
                fontWeight: character.npcControlMode === mode ? 600 : 400,
                backgroundColor: character.npcControlMode === mode
                  ? mode === 'ai' ? 'rgba(147, 51, 234, 0.3)' : 'rgba(192, 192, 192, 0.2)'
                  : 'rgba(0,0,0,0.3)',
                color: character.npcControlMode === mode
                  ? mode === 'ai' ? '#a855f7' : '#d4d4d8'
                  : 'rgba(160, 150, 180, 0.7)',
                border: character.npcControlMode === mode
                  ? `1px solid ${mode === 'ai' ? 'rgba(147, 51, 234, 0.5)' : 'rgba(192, 192, 192, 0.4)'}`
                  : '1px solid rgba(100, 80, 120, 0.2)',
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {controlLabels[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* Personality */}
      <div style={{ marginBottom: 12 }}>
        <div style={{
          fontSize: 10,
          color: 'rgba(160, 150, 180, 0.6)',
          marginBottom: 6,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Personality
        </div>
        <textarea
          value={character.npcPersonality || ''}
          onChange={(e) => updateCharacter(character.id, { npcPersonality: e.target.value })}
          placeholder="e.g., Cautious healer. Prioritizes keeping Bobby alive."
          style={{
            width: '100%',
            padding: 8,
            fontSize: 11,
            fontFamily: 'Georgia, serif',
            color: character.npcPersonality ? '#f0e8f8' : 'rgba(160, 150, 180, 0.5)',
            fontStyle: character.npcPersonality ? 'normal' : 'italic',
            backgroundColor: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(100, 80, 120, 0.25)',
            borderRadius: 3,
            resize: 'vertical',
            minHeight: 48,
            lineHeight: 1.4,
          }}
        />
      </div>

      {/* Tactics */}
      <div>
        <div style={{
          fontSize: 10,
          color: 'rgba(160, 150, 180, 0.6)',
          marginBottom: 6,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Combat Tactics
        </div>
        <textarea
          value={character.npcTactics || ''}
          onChange={(e) => updateCharacter(character.id, { npcTactics: e.target.value })}
          placeholder="e.g., Healing Word on lowest-HP ally. Spiritual Weapon if no one hurt."
          style={{
            width: '100%',
            padding: 8,
            fontSize: 11,
            fontFamily: 'Georgia, serif',
            color: character.npcTactics ? '#f0e8f8' : 'rgba(160, 150, 180, 0.5)',
            fontStyle: character.npcTactics ? 'normal' : 'italic',
            backgroundColor: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(100, 80, 120, 0.25)',
            borderRadius: 3,
            resize: 'vertical',
            minHeight: 48,
            lineHeight: 1.4,
          }}
        />
      </div>
    </ParchmentSection>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function PartyPanel() {
  const partySummaries = useCharacterStore((s) => s.getPartySummaries());
  const flyoutId = useCharacterStore((s) => s.flyoutCharacterId);
  const showFlyout = useCharacterStore((s) => s.showFlyout);
  const getCharacter = useCharacterStore((s) => s.getCharacter);
  const setAbsent = useCharacterStore((s) => s.setAbsent);

  // Combat/Initiative - auto-sort when initiative is rolled
  const { inCombat, initiativeOrder } = useCombatStore();

  // Demo party initialization removed - using Supabase persistence now

  const flyoutCharacter = flyoutId ? getCharacter(flyoutId) : null;

  // Helper to get initiative from the subscribed array (triggers re-renders)
  const getInitiativeFor = (characterId: string) => {
    const entry = initiativeOrder.find(e => e.characterId === characterId);
    return entry?.initiative ?? null;
  };

  // Sort party by initiative when in combat (highest first)
  const sortedSummaries = inCombat && initiativeOrder.length > 0
    ? [...partySummaries].sort((a, b) => {
        const initA = getInitiativeFor(a.id) ?? -999;
        const initB = getInitiativeFor(b.id) ?? -999;
        return initB - initA; // Descending (highest first)
      })
    : partySummaries;

  return (
    <>
      <div>
        <h4
          style={{
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-sm)',
          }}
        >
          Party Members
        </h4>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xs)',
          }}
        >
          {sortedSummaries.map((summary, index) => (
            <PartyMemberRow
              key={summary.id}
              summary={summary}
              isSelected={flyoutId === summary.id}
              onShowFlyout={() => showFlyout(flyoutId === summary.id ? null : summary.id)}
              onToggleAbsent={() => setAbsent(summary.id, !summary.absentThisSession)}
              index={index}
            />
          ))}
        </div>
        <div
          style={{
            marginTop: 'var(--spacing-sm)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
          }}
        >
          Click to view details
        </div>
      </div>

      {/* Flyout */}
      {flyoutCharacter && (
        <CharacterFlyout
          character={flyoutCharacter}
          onClose={() => showFlyout(null)}
          index={partySummaries.findIndex(s => s.id === flyoutId)}
        />
      )}
    </>
  );
}
